import { useState } from 'react';
import { Badge, Button, ErrorBanner, Skeleton, useApi, useApiQuery } from '../../engine';
import { ApiError } from '../../engine/api/client';
import { formatDateTime } from '../../lib/format';

// "Integrations" — a section of the Settings screen (SettingsScreen.tsx is the engine exception;
// this is one of its cards). What each outbound integration runs on — the deployment's environment,
// this organisation's settings, a built-in default, or nothing — and a Test that checks it live.
// GET /v1/integrations/status, POST /v1/integrations/status/:key/test (owner/admin; a member gets
// 403 and the card stays hidden). Environment variables are shown by NAME only; the API never
// returns their values.

export interface IntegrationStatus {
  key: 'email' | 'storage' | 'ai' | 'halo';
  label: string;
  source: 'env' | 'settings' | 'default' | 'none';
  configured: boolean;
  env: string[];
  detail: string;
  testable: boolean;
}
interface TestResult { key: string; ok: boolean; detail: string; checked_at: string }

const SOURCE: Record<IntegrationStatus['source'], { label: string; tone: 'info' | 'accent' | 'muted' | 'warn' }> = {
  env: { label: 'configured (env)', tone: 'info' },
  settings: { label: 'configured (settings)', tone: 'accent' },
  default: { label: 'built-in default', tone: 'muted' },
  none: { label: 'not configured', tone: 'warn' },
};

/** The badge for one integration: where its configuration comes from, or that it is incomplete. */
export function sourceBadge(s: Pick<IntegrationStatus, 'source' | 'configured'>): { label: string; tone: 'info' | 'accent' | 'muted' | 'warn' } {
  if (s.source !== 'none' && !s.configured) return { label: `${SOURCE[s.source].label.replace(/^configured /, '')} — incomplete`, tone: 'warn' };
  return SOURCE[s.source];
}

export function IntegrationStatusCard() {
  const api = useApi();
  const q = useApiQuery<{ data: IntegrationStatus[] }>(['integrations-status'], '/v1/integrations/status');
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [busy, setBusy] = useState<string | null>(null);
  if (q.error instanceof ApiError && q.error.status === 403) return null;
  const run = async (key: string) => {
    setBusy(key);
    try {
      const r = await api.request<TestResult>(`/v1/integrations/status/${encodeURIComponent(key)}/test`, { method: 'POST' });
      setResults((prev) => ({ ...prev, [key]: r }));
    } catch (e) {
      setResults((prev) => ({ ...prev, [key]: { key, ok: false, detail: e instanceof ApiError ? e.detail : String(e), checked_at: new Date().toISOString() } }));
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="hf-card pad hf-stack" aria-labelledby="integrations-status-title">
      <div className="hf-caps" id="integrations-status-title">Integrations</div>
      <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-13)' }}>Where each connection gets its configuration. “env” is set by whoever runs this deployment; “settings” is configured here in NovaPSA.</p>
      {q.isLoading ? <Skeleton lines={4} /> : q.error ? <ErrorBanner error={q.error} /> : (
        <ul className="hf-integrations" aria-label="Integration status">
          {(q.data?.data ?? []).map((s) => {
            const b = sourceBadge(s);
            const r = results[s.key];
            return (
              <li key={s.key} className="hf-integration">
                <div className="head">
                  <strong>{s.label}</strong>
                  <Badge tone={b.tone} dot={false}>{b.label}</Badge>
                  {s.testable && <Button size="sm" variant="outline" busy={busy === s.key} onClick={() => void run(s.key)} aria-label={`Test ${s.label}`}>Test</Button>}
                </div>
                <div className="hf-muted detail">{s.detail}</div>
                {s.env.length > 0 && <div className="env">{s.env.map((n) => <code key={n}>{n}</code>)}</div>}
                {r && (
                  <div role="status" className={r.ok ? 'result ok' : 'result bad'}>
                    {r.ok ? '✓' : '✗'} {r.detail} <span className="hf-muted">· {formatDateTime(r.checked_at)}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
